import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import type { ReactElement } from 'react';
import { Spin } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { EntityRelatedList } from '../../../components/entity-detail/EntityRelatedList.js';
import { EntityTaskDueChip } from '../../../components/entity-detail/EntityTaskDueChip.js';
import type { TaskItem } from '../api/tasks.client.js';
import {
  getTaskPriorityLabel,
  getTaskStatusLabel,
  PRIORITY_TONE,
  STATUS_TONE,
} from '../lib/taskMeta.js';

interface RelatedTasksPanelProps {
  tasks: TaskItem[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
  hasLinks: boolean;
}

export function RelatedTasksPanel({
  tasks,
  isLoading,
  isError,
  error,
  onRetry,
  hasLinks,
}: RelatedTasksPanelProps): ReactElement {
  const { t } = useTranslation();
  const prefix = 'crm.features.features.tasks.components.relatedtaskspanel.';
  if (isError)
    return (
      <ErrorState
        title={t(`${prefix}не_удалось_загрузить_связанные_задачи`)}
        error={error}
        onRetry={onRetry}
      />
    );
  if (isLoading)
    return (
      <div className="flex justify-center py-6">
        <Spin />
      </div>
    );
  return (
    <EntityRelatedList
      emptyText={t(
        `${prefix}${hasLinks ? 'связанных_задач_пока_нет' : 'у_задачи_пока_нет_связей_привяжите_сделку_'}`,
      )}
      rows={tasks.map((task) => ({
        id: task.id,
        title: task.title,
        href: `/tasks/${task.id}`,
        status: (
          <>
            <EntityStatusChip tone={STATUS_TONE[task.status]}>
              {getTaskStatusLabel(task.status)}
            </EntityStatusChip>
            <EntityStatusChip tone={PRIORITY_TONE[task.priority]}>
              {getTaskPriorityLabel(task.priority)}
            </EntityStatusChip>
          </>
        ),
        meta:
          task.assigneeMember?.user.name ||
          task.assigneeMember?.user.email ||
          t('crm.features.task_detail.unassigned'),
        value: (
          <EntityTaskDueChip
            dueAt={task.dueAt ?? null}
            closed={task.status === 'done' || task.status === 'cancelled'}
          />
        ),
      }))}
    />
  );
}
